import { EXAM_RULES } from "@eb/shared";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Flame, Layers, Target, Timer, TrendingUp } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Card, ErrorBanner, ProgressBar, Spinner, cx } from "../components/ui";
import { api } from "../lib/api";
import { formatPercent } from "../lib/format";
import { useAuth } from "../state/auth";

function StatTile({
    icon,
    label,
    value,
    hint,
    tone
}: {
    icon: ReactNode;
    label: string;
    value: string;
    hint?: string;
    tone?: "brand" | "good" | "warn";
}) {
    const accent = tone === "good" ? "text-good" : tone === "warn" ? "text-warn" : "text-brand";
    return (
        <Card className="p-4">
            <div className="flex items-center gap-2 text-xs text-ink-soft">
                <span className={accent}>{icon}</span>
                {label}
            </div>
            <p className="mt-2 text-2xl font-semibold text-ink tabular-nums">{value}</p>
            {hint ? <p className="mt-0.5 text-xs text-ink-soft">{hint}</p> : null}
        </Card>
    );
}

export function DashboardPage() {
    const { displayName } = useAuth();
    const statsQuery = useQuery({ queryKey: ["dashboard"], queryFn: () => api.stats(30) });

    if (statsQuery.isLoading) return <Spinner label="正在整理你的进度" />;
    if (statsQuery.isError || !statsQuery.data) {
        return <ErrorBanner message="加载概览失败" onRetry={() => void statsQuery.refetch()} />;
    }

    const { overview } = statsQuery.data;
    const studied = overview.totalQuestions - overview.newCount;
    const readyTone = overview.estimatedPassRate >= 0.8 ? "good" : overview.estimatedPassRate >= 0.5 ? "warn" : "brand";

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold text-ink">你好，{displayName}</h1>
                    <p className="mt-1 text-sm text-ink-soft">
                        今天有 <strong className="text-ink">{overview.dueToday}</strong> 张卡片到期，
                        还有 <strong className="text-ink">{overview.newCount}</strong> 道题没学过。
                    </p>
                </div>
                <Link to="/study">
                    <Button>
                        开始学习
                        <ArrowRight className="size-4" aria-hidden />
                    </Button>
                </Link>
            </div>

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <StatTile
                    icon={<Target className="size-4" aria-hidden />}
                    label="预估通过率"
                    value={formatPercent(overview.estimatedPassRate)}
                    hint={`${EXAM_RULES.totalQuestions} 题答对 ${EXAM_RULES.passingScore} 题即通过`}
                    tone={readyTone}
                />
                <StatTile
                    icon={<Layers className="size-4" aria-hidden />}
                    label="已掌握"
                    value={`${overview.matureCount}`}
                    hint="记忆稳定度超过 21 天的题"
                    tone="good"
                />
                <StatTile
                    icon={<Flame className="size-4" aria-hidden />}
                    label="连续学习"
                    value={`${overview.streakDays} 天`}
                    hint={`最长纪录 ${overview.longestStreakDays} 天`}
                    tone="warn"
                />
                <StatTile
                    icon={<TrendingUp className="size-4" aria-hidden />}
                    label="近期正确率"
                    value={formatPercent(overview.recentAccuracy)}
                    hint={`累计 ${overview.totalReviews} 次作答`}
                />
            </div>

            <Card className="space-y-4 p-5">
                <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-ink">题库进度</h2>
                    <Badge tone="neutral">
                        {studied} / {overview.totalQuestions}
                    </Badge>
                </div>
                <ProgressBar value={studied} max={overview.totalQuestions} />
                <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-5">
                    {[
                        { label: "未学", value: overview.newCount },
                        { label: "学习中", value: overview.learningCount },
                        { label: "复习中", value: overview.reviewCount },
                        { label: "重新学习", value: overview.relearningCount }
                    ].map((item) => (
                        <div key={item.label} className="rounded-xl bg-surface-sunken px-3 py-2">
                            <dt className="text-xs text-ink-soft">{item.label}</dt>
                            <dd className="text-lg font-semibold text-ink tabular-nums">{item.value}</dd>
                        </div>
                    ))}
                    {/* The only tile that is a destination: the collection has its own page. */}
                    <Link
                        to="/mistakes"
                        className={cx(
                            "rounded-xl px-3 py-2 transition-colors",
                            overview.mistakeCount > 0
                                ? "bg-bad-soft hover:bg-bad-soft/70"
                                : "bg-surface-sunken hover:bg-line/60"
                        )}
                    >
                        <dt className="text-xs text-ink-soft">错题集</dt>
                        <dd
                            className={cx(
                                "text-lg font-semibold tabular-nums",
                                overview.mistakeCount > 0 ? "text-bad" : "text-ink"
                            )}
                        >
                            {overview.mistakeCount}
                        </dd>
                    </Link>
                </dl>
            </Card>

            <div className="grid gap-4 lg:grid-cols-2">
                <Card className="flex flex-col justify-between gap-4 p-5">
                    <div>
                        <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
                            <Timer className="size-4 text-brand" aria-hidden />
                            来一次模拟考
                        </h2>
                        <p className="mt-1.5 text-sm text-ink-soft">
                            完全按官方规则：{EXAM_RULES.federalQuestions} 道联邦题 +{" "}
                            {EXAM_RULES.stateQuestions} 道州题，{EXAM_RULES.durationMinutes} 分钟，答对{" "}
                            {EXAM_RULES.passingScore} 题通过。做完的题会自动进入复习计划。
                        </p>
                    </div>
                    <Link to="/exam">
                        <Button variant="secondary">进入模拟考</Button>
                    </Link>
                </Card>

                <Card className="p-5">
                    <h2 className="text-sm font-semibold text-ink">最近容易错的题</h2>
                    {statsQuery.data.weakest.length === 0 ? (
                        <p className="mt-2 text-sm text-ink-soft">还没有错题记录，先去学几张卡片吧。</p>
                    ) : (
                        <ul className="mt-3 space-y-2">
                            {statsQuery.data.weakest.slice(0, 4).map((item) => (
                                <li key={item.question.number}>
                                    <Link
                                        to={`/questions/${item.question.number}`}
                                        className="flex items-start gap-3 rounded-xl px-2 py-1.5 transition-colors hover:bg-surface-sunken"
                                    >
                                        <span className="mt-0.5 text-xs text-ink-soft tabular-nums">
                                            #{item.question.number}
                                        </span>
                                        <span lang="de" className="flex-1 line-clamp-2 text-sm text-ink">
                                            {item.question.text}
                                        </span>
                                        <span className="shrink-0 text-xs text-bad tabular-nums">
                                            {formatPercent(item.accuracy)}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                    <Link
                        to="/study?filter=DIFFICULT"
                        className="mt-3 inline-block text-sm font-medium text-brand hover:underline"
                    >
                        专门练易错题 →
                    </Link>
                </Card>
            </div>
        </div>
    );
}
